import {
	DictionaryParser,
	DictionaryResults,
	DictionaryText
} from "shared/Dictionary/DictionarySource";

export type OptionalLangOupEntry = {
	text: string;
	language_code?: string;
	prefix?: string;
	audio?: string;
	phonetic?: string;
};

export type OupEntry = OptionalLangOupEntry & {
	language_code: string;
};

export type OupDefinition = OupEntry & {
	translations: OupEntry[];
	examples?: OupEntry[];
};

export type OupWord = {
	word_id: number;
	text: string;
	pronunciations: OupEntry[];
	part_of_speech: string;
	definitions?: OupDefinition[];
	related_words: string[];
	bold_forms?: Array<{
		text: string;
		word_id: number
	}>;
	irregular_forms?: OptionalLangOupEntry[];
	examples?: OupEntry[];
	verb_patterns: OupEntry[];
	label?: string;
};

export type OupSchema = OupWord[];

export class OupDictionaryEntryParser implements DictionaryParser<OupSchema> {
	constructor(
		private audioUrlBuilder: (wavPath: string) => string,
		private lang: string,
		private translateLang?: string
	) { }

	private getValidDefinition(word: OupWord): DictionaryText[] {
		if (!word.definitions) {
			return [];
		}
		return word.definitions.reduce<DictionaryText[]>((agg, element) => {
			if (element.text && element.language_code == this.lang) {
				agg.push(element);
			}
			if (element.translations && element.translations.length && this.translateLang) {
				const translateLang = this.translateLang;
				element.translations.forEach (function (value) {
					if (value.language_code == translateLang) {
						agg.push(value);
					}
					return;
				})
			}
			return agg;
		}, []);
	}

	private getValidExamples(word: OupWord): undefined|string[] {
		if (word.examples === undefined) {
			return undefined;
		}
		return word.examples.filter(e => (e.text && e.language_code === this.lang)).map(e => e.text);
	}

	private getAudioLang(word: OupWord): string|undefined {
		if (!word.pronunciations) {
			return undefined;
		}
		const langAudio = word.pronunciations.reduce<string|undefined>((agg, entry) => {
			return entry.language_code == this.lang && entry.audio ? entry.audio : agg;
		}, undefined);
		return langAudio ? this.audioUrlBuilder(langAudio) : undefined;
	}

	private getPhoneticLang(word: OupWord): string|undefined {
		if (!word.pronunciations) {
			return undefined;
		}
		return word.pronunciations.reduce((agg, entry) => {
			return (entry.language_code === this.lang && entry.phonetic) ? entry.phonetic : agg;
		}, "");
	}

	private filterRelatedWords(word: OupWord): string[] {
		if (!word.related_words) {
			return [];
		}

		return word.related_words.filter(w => w != word.text);
	}

	private getBoldForms(word: OupWord): string[] {
		if (!word.bold_forms) {
			return [];
		}
		return word.bold_forms.map(e => e.text).filter(Boolean).slice(0, 5);
	}

	public parse(input: OupSchema): DictionaryResults {

		return {
			suggestions: [],
			entries:     input.map((oupWord) => ({
				headword:      oupWord.text || "",
				partOfSpeech:  oupWord.part_of_speech || undefined,
				label:         oupWord.label,
				definitions:   this.getValidDefinition(oupWord),
				pronunciation: this.getPhoneticLang(oupWord),
				wavFile:       this.getAudioLang(oupWord),
				example:       this.getValidExamples(oupWord),
				alt:           this.filterRelatedWords(oupWord),
				boldForms:     this.getBoldForms(oupWord)
			}))
		};
	}
}
